import { collection } from '@/infra/collection'
import {
  BusinessProfileVersion,
  BusinessProfileVersionId,
} from './BusinessProfile'
import type { TaxProfileApi } from '@/features/tax-profile/api/tax-profile-api'
import { Id } from '@/infra/id'
import { debounceTime, groupBy, mergeMap, Subject, switchMap } from 'rxjs'
import { withAbortSignal } from '@/utils/rx-util'
import { onRpcNotFoundReturnNull } from '@/utils/grpc-util'
import { mapBusinessProfileProtoToProps } from './business-profile-mappers'
import {
  GetBusinessProfileRequest as GetBusinessProfileRequestProto,
  GetBusinessProfileForJobRequest as GetBusinessProfileForJobRequestProto,
  CreateBusinessProfileRequest as CreateBusinessProfileRequestProto,
  UpdateBusinessProfileRequest as UpdateBusinessProfileRequestProto,
  ListBusinessProfilesRequest as ListBusinessProfilesRequestProto,
  DelistBusinessProfileRequest as DelistBusinessProfileRequestProto,
} from '@taxfyle/api-internal/internal/business_profile_pb'
import {
  mapBusinessInfoToProto,
  mapContactInfoToProto,
} from './business-profile-mappers'
import type { BusinessInfo } from './BusinessInfo'
import {
  ContactInfo,
  type ContactInfo as ContactInfoType,
} from '@/features/tax-profile/ContactInfo'
import type { GetCurrentMemberFn } from '@/features/iam'
import type { CursorPageQuery } from '@/infra/paging'

/**
 * Store for managing business profiles.
 */
export class BusinessProfileStore {
  /**
   * Subject to emit on which triggers a refresh of the latest business profile version.
   */
  private readonly _refreshLatestBusinessProfileVersion = new Subject<Id>()

  /**
   * Emits when a business profile is delisted.
   */
  readonly businessProfileDelisted$ = new Subject<Id>()

  /**
   * Collection of business profile versions.
   */
  readonly businessProfileVersions = collection({
    create: () => new BusinessProfileVersion(),
    getDataId: (d) => BusinessProfileVersionId.from(d.id, d.version),
    getModelId: (m) => BusinessProfileVersionId.from(m.id, m.version),
  })

  constructor(
    private readonly _api: TaxProfileApi,
    private readonly _getCurrentMember: GetCurrentMemberFn,
  ) {
    this._refreshLatestBusinessProfileVersion
      .pipe(
        groupBy((profileId) => profileId, {
          duration: (source$) => source$.pipe(debounceTime(5_000)),
        }),
        mergeMap((profileId$) =>
          profileId$.pipe(
            switchMap((profileId) =>
              withAbortSignal((signal) =>
                this.fetchBusinessProfile(profileId, signal),
              ),
            ),
          ),
        ),
      )
      .subscribe()
  }

  /**
   * Fetches a business profile by ID.
   */
  async fetchBusinessProfile(id: Id, signal?: AbortSignal) {
    signal?.throwIfAborted()
    const request = new GetBusinessProfileRequestProto().setId(Id.assert(id))
    const response = await this._api.businessProfiles
      .getBusinessProfile(request)
      .catch(onRpcNotFoundReturnNull)
    signal?.throwIfAborted()
    if (!response) {
      return null
    }
    const props = mapBusinessProfileProtoToProps(response)
    return this.businessProfileVersions.set(props)
  }

  /**
   * Fetches the business profile for a job.
   */
  async fetchBusinessProfileForJob(jobId: Id, signal?: AbortSignal) {
    signal?.throwIfAborted()
    const request = new GetBusinessProfileForJobRequestProto().setJobId(
      Id.assert(jobId),
    )
    const response = await this._api.businessProfiles
      .getBusinessProfileForJob(request)
      .catch(onRpcNotFoundReturnNull)
    signal?.throwIfAborted()
    if (!response) {
      return null
    }
    const props = mapBusinessProfileProtoToProps(response)
    return this.businessProfileVersions.set(props)
  }

  /**
   * Delists a business profile.
   */
  async delistBusinessProfile(id: Id) {
    const request = new DelistBusinessProfileRequestProto().setId(Id.assert(id))
    await this._api.businessProfiles.delistBusinessProfile(request)
    this.businessProfileDelisted$.next(id)
  }

  /**
   * Saves a business profile version (creates or updates).
   */
  async saveBusinessProfileVersion(
    input: SaveBusinessProfileVersionOptions,
  ): Promise<BusinessProfileVersion> {
    const isUpdate = !!input.id

    // Map the common business info and contact info
    const businessInfoProto = mapBusinessInfoToProto(input.businessInfo)
    const contactInfoProto = input.contactInfo
      ? mapContactInfoToProto(input.contactInfo)
      : undefined

    let id: string
    let version: number
    const workspaceId = Id.assert(this._getCurrentMember().workspaceId)

    if (!isUpdate) {
      // Create a new profile
      const request = new CreateBusinessProfileRequestProto()
        .setWorkspaceId(workspaceId)
        .setBusinessInfo(businessInfoProto)

      if (contactInfoProto) {
        request.setContactInfo(contactInfoProto)
      }

      const response =
        await this._api.businessProfiles.createBusinessProfile(request)

      id = response.getId()
      version = response.getVersion()
    } else {
      // Update existing profile
      const request = new UpdateBusinessProfileRequestProto()
        .setId(Id.assert(input.id))
        .setBusinessInfo(businessInfoProto)

      if (contactInfoProto) {
        request.setContactInfo(contactInfoProto)
      }

      const response =
        await this._api.businessProfiles.updateBusinessProfile(request)

      id = response.getId()
      version = response.getVersion()
    }

    // The info is more or less the same except for some vanity formatting,
    // so we can just set it as-is.
    const model = this.businessProfileVersions.set({
      id,
      version,
      workspaceId,
      businessInfo: input.businessInfo,
      contactInfo: input.contactInfo ?? ContactInfo.empty,
    })

    // Refresh in the background so we pick up on the latest version.
    this._refreshLatestBusinessProfileVersion.next(id)

    return model
  }

  /**
   * Lists business profiles.
   */
  async listBusinessProfiles(
    query: ListBusinessProfilesQuery,
    signal?: AbortSignal,
  ) {
    signal?.throwIfAborted()
    const workspaceId = Id.assert(this._getCurrentMember().workspaceId)
    const request = new ListBusinessProfilesRequestProto().setWorkspaceId(
      workspaceId,
    )

    if (query.paging?.after) {
      request.setPageToken(query.paging.after)
    }
    if (query.paging?.limit) {
      request.setPageSize(query.paging.limit)
    }

    const response =
      await this._api.businessProfiles.listBusinessProfiles(request)
    signal?.throwIfAborted()

    const props = response
      .getBusinessProfilesList()
      .map((proto) => mapBusinessProfileProtoToProps(proto))
    const items = this.businessProfileVersions.set(props)

    return {
      data: items,
      next: response.getNextPageToken() || null,
      total: response.getTotalSize(),
    }
  }
}

/**
 * Query for listing business profiles.
 */
export interface ListBusinessProfilesQuery {
  paging?: CursorPageQuery
}

/**
 * Options for saving a business profile version.
 */
export interface SaveBusinessProfileVersionOptions {
  id?: Id
  businessInfo: BusinessInfo
  contactInfo?: ContactInfoType
}

/**
 * Options for listing business profiles.
 */
export interface ListBusinessProfilesOptions {
  pageSize?: number
  pageToken?: string
}
